'use client';

import { createContext, useContext, useEffect, useRef, useState } from 'react';

type FilterChipSelectContextType = {
  /** Whether the dropdown is open */
  isOpen: boolean;
  /** Function to set the open state */
  setOpen: (open: boolean) => void;
  /** Selection type, either 'single' (false) or 'multiple' (true) */
  multiple?: boolean;
  /** Currently selected value(s) */
  value?: string | string[];
  /** Default value(s) when uncontrolled */
  defaultValue?: string | string[];
  /** Callback when selection changes. `undefined` signals deselection in single-select mode. */
  onCheckedChange?: (value: string | string[] | undefined) => void;
  /** Max items to display on trigger when selected (ignored when type is 'single') */
  maxItemsToShow?: number;
  /** Dynamically extracted options from children, stored in context */
  options?: Map<string, { value: string; label?: string }>;
  /** The value of the currently highlighted item (hovered or keyboard-focused). `undefined` when nothing is highlighted. */
  highlightedValue?: string;
  /** Set the highlighted item value. Called by item slots on focus/hover. */
  setHighlightedValue: (value: string | undefined) => void;
};

const FilterChipSelectContext = createContext<FilterChipSelectContextType>({
  isOpen: false,
  setOpen: () => undefined,
  multiple: true,
  value: [],
  defaultValue: undefined,
  onCheckedChange: () => undefined,
  maxItemsToShow: 1,
  options: new Map<string, { value: string; label?: string }>(),
  highlightedValue: undefined,
  setHighlightedValue: () => undefined,
});

export type FilterChipSelectProviderProps = {
  children: React.ReactNode;
  value: Omit<FilterChipSelectContextType, 'highlightedValue' | 'setHighlightedValue'>;
};

export const FilterChipSelectProvider: React.FC<FilterChipSelectProviderProps> = ({ children, value: selectProviderValue }) => {
  const [internalValue, setInternalValue] = useState<string | string[] | undefined>(selectProviderValue.defaultValue);
  const [highlightedValue, setHighlightedValue] = useState<string | undefined>(undefined);

  // When value transitions from defined to undefined, reset internal state. This handles
  // cases like "Clear All" in DataGrid where the parent clears by passing undefined, which
  // would otherwise leave FilterChipSelect in stale uncontrolled mode. Mirrors the same fix
  // in FilterChipDate.
  const prevValueRef = useRef(selectProviderValue.value);
  if (prevValueRef.current !== undefined && selectProviderValue.value === undefined) {
    setInternalValue(undefined);
  }
  prevValueRef.current = selectProviderValue.value;

  // Reset highlighted value when the menu closes.
  useEffect(() => {
    if (!selectProviderValue.isOpen) {
      setHighlightedValue(undefined);
    }
  }, [selectProviderValue.isOpen]);

  const isControlled = selectProviderValue.value !== undefined;
  const currentValue = isControlled ? selectProviderValue.value : internalValue;

  const handleCheckedChange = (value: string | string[] | undefined) => {
    if (!isControlled) {
      setInternalValue(value);
    }
    selectProviderValue.onCheckedChange?.(value);
  };

  const value = {
    ...selectProviderValue,
    value: currentValue,
    onCheckedChange: handleCheckedChange,
    highlightedValue,
    setHighlightedValue,
  };

  return <FilterChipSelectContext.Provider value={value}>{children}</FilterChipSelectContext.Provider>;
};

export const useFilterChipSelect = () => {
  return useContext(FilterChipSelectContext);
};
